/**
 * Resolve multi-country bulk edit config into per-country API payloads.
 */

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { parseIntervalHHMM } from '@/features/smswiser-campaigns/utils/scheduleInterval';
import { groupItemsByCountryKey } from '@/features/smswiser-campaigns/utils/preparedDropBulkByCountry';
import { shortlinkPick } from '@/features/smswiser-campaigns/utils/shortlinkOptionState';
import type { BulkUpdatePreparedDropsPayload } from '@/hooks';

import { computeMultiCountryIntervals } from './computeMultiCountryIntervals';
import type {
  MultiCountryBulkEditConfig,
  PerItemScheduleEntry,
  ResolvedCountryPayload,
} from './multiCountryBulkEdit.types';
import { slicePerDropRowsForItemIds } from './slicePerDropRowsForItemIds';

function buildSharedPayload(cfg: MultiCountryBulkEditConfig): BulkUpdatePreparedDropsPayload {
  const p: BulkUpdatePreparedDropsPayload = {};
  if (cfg.amount !== undefined) p.amount = cfg.amount;
  if (cfg.shortLinkTldValue !== undefined) p.shortLinkTldValue = cfg.shortLinkTldValue;
  if (cfg.executeAfter !== undefined) p.executeAfter = cfg.executeAfter;
  if (cfg.staticTagId !== 'unchanged' && cfg.staticTagId !== 'mixed') {
    p.staticTagId = cfg.staticTagId;
  }
  if (cfg.forceSend !== 'unchanged' && cfg.forceSend !== 'mixed') {
    p.forceSend = cfg.forceSend;
  }
  if (cfg.batchAmount !== undefined) p.batchAmount = cfg.batchAmount;
  if (cfg.batchDelay !== undefined) p.batchDelay = cfg.batchDelay;
  if (cfg.useOnlyNewDomains !== 'unchanged' && cfg.useOnlyNewDomains !== 'mixed') {
    p.useOnlyNewDomains = cfg.useOnlyNewDomains;
  }
  const clickId = cfg.clickIdStructure ? shortlinkPick(cfg.clickIdStructure) : undefined;
  if (clickId) p.clickIdStructure = clickId;
  const scheme = cfg.domainScheme ? shortlinkPick(cfg.domainScheme) : undefined;
  if (scheme) p.domainScheme = scheme;
  if (cfg.aiOptimizationEnabled !== undefined) p.aiOptimizationEnabled = cfg.aiOptimizationEnabled;
  if (cfg.aiOptimizationType !== undefined) p.aiOptimizationType = cfg.aiOptimizationType;
  if (cfg.aiOptimizationBatchAmount !== undefined) p.aiOptimizationBatchAmount = cfg.aiOptimizationBatchAmount;
  if (cfg.aiOptimizationMinimalValuePercent !== undefined) {
    p.aiOptimizationMinimalValuePercent = cfg.aiOptimizationMinimalValuePercent;
  }
  if (cfg.aiOptimizationSendUntil !== undefined) p.aiOptimizationSendUntil = cfg.aiOptimizationSendUntil;
  if (cfg.senderNames?.length) p.senderNames = cfg.senderNames;
  if (cfg.smsTexts?.length) p.smsTexts = cfg.smsTexts;
  return p;
}

function applyCountryOverride(
  base: BulkUpdatePreparedDropsPayload,
  countryKey: string,
  cfg: MultiCountryBulkEditConfig,
): BulkUpdatePreparedDropsPayload {
  const p = { ...base };
  const ov = cfg.overridesByCountry[countryKey];
  if (ov) {
    if (ov.routeId !== undefined) p.routeId = ov.routeId;
    if (ov.wiserId !== undefined) p.wiserId = ov.wiserId;
    if (ov.shortLinkTldValue !== undefined) p.shortLinkTldValue = ov.shortLinkTldValue;
  }
  const countryTexts = cfg.smsTextsByCountry?.[countryKey];
  if (countryTexts?.length) p.smsTexts = countryTexts;
  const countrySenders = cfg.senderNamesByCountry?.[countryKey];
  if (countrySenders?.length) p.senderNames = countrySenders;
  return p;
}

function withSlicedPerDropCreative(
  orderedIds: string[],
  cfg: MultiCountryBulkEditConfig,
  itemIds: string[],
  payload: BulkUpdatePreparedDropsPayload,
): BulkUpdatePreparedDropsPayload {
  const texts = slicePerDropRowsForItemIds(orderedIds, cfg.smsTextsPerDrop, itemIds);
  const senders = slicePerDropRowsForItemIds(orderedIds, cfg.senderNamesPerDrop, itemIds);
  const next: BulkUpdatePreparedDropsPayload = { ...payload };
  if (texts?.length) next.smsTextsPerDrop = texts;
  if (senders?.length) next.senderNamesPerDrop = senders;
  return next;
}

export interface BuildMultiCountryPayloadsInput {
  ids: string[];
  mergedDrops: PreparedDrop[];
  config: MultiCountryBulkEditConfig;
}

export type BuildMultiCountryPayloadsResult =
  | { type: 'grouped'; payloads: ResolvedCountryPayload[] }
  | { type: 'per_item'; entries: PerItemScheduleEntry[] };

export function buildMultiCountryPayloads(
  input: BuildMultiCountryPayloadsInput,
): BuildMultiCountryPayloadsResult {
  const { ids, mergedDrops, config } = input;
  const sharedPayload = buildSharedPayload(config);
  const intervalMinutes = parseIntervalHHMM(config.scheduleInterval);
  const hasInterval = intervalMinutes > 0 && config.executeAfter;

  if (hasInterval) {
    const entries = computeMultiCountryIntervals({
      ids,
      mergedDrops,
      baseUtcIso: config.executeAfter!,
      intervalMinutes,
      mode: config.intervalMode,
    });
    return {
      type: 'per_item',
      entries: entries.map((e) => {
        const merged = {
          ...sharedPayload,
          ...e.payload,
        };
        const withOverride = applyCountryOverride(merged, e.countryKey, config);
        const idx = ids.indexOf(e.id);
        if (idx < 0) return { ...e, payload: withOverride };
        const rowIds = [e.id];
        return { ...e, payload: withSlicedPerDropCreative(ids, config, rowIds, withOverride) };
      }),
    };
  }

  const items = ids
    .map((id) => mergedDrops.find((d) => d.id === id))
    .filter((d): d is PreparedDrop => Boolean(d))
    .map((d) => ({ id: d.id, wiserId: d.wiserId }));

  const byCountry = groupItemsByCountryKey(items, mergedDrops);
  const payloads: ResolvedCountryPayload[] = [];
  for (const [ck, groupItems] of byCountry) {
    const itemIds = groupItems.map((i) => i.id);
    const base = applyCountryOverride(sharedPayload, ck, config);
    payloads.push({
      countryKey: ck,
      items: groupItems,
      payload: withSlicedPerDropCreative(ids, config, itemIds, base),
    });
  }
  return { type: 'grouped', payloads };
}
